<template>
  <div class="grid-cache-keys">
    <div v-if="loading" class="loader"></div>
    <div v-else>
      <div class="buttons-cnt">
        <backstage-button
          class="button-delete"
          :variant="ButtonVariant.SECONDARY"
          :disabled="!selectedCount"
          @click="deleteSelected">
          {{ t('admin.cache.delete_selected') }} ({{ selectedCount }})
        </backstage-button>
      </div>
      <ag-grid-vue
        v-if="rowData.length"
        :key="pattern"
        class="ag-theme-balham"
        data-cy="grid-cache-keys"
        :grid-options="gridOptions"
        :column-defs="columnDefinitions"
        :default-col-def="defaultColDef"
        :row-data="rowData"
        :pagination="true"
        :pagination-page-size="pageSize"
        :suppress-menu-hide="true"
        :modules="modules"
        @first-data-rendered="onFirstDataRendered"
        @selection-changed="onSelectionChanged">
      </ag-grid-vue>
      <p v-else class="no-keys-found">{{ t('admin.cache.no_keys_found') }}</p>
      <div v-if="hasMore" class="load-more-cnt">
        <backstage-button :variant="ButtonVariant.SECONDARY" @click="loadMore">
          {{ t('admin.cache.load_more') }}
        </backstage-button>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import BackstageButton from '@/components/common/BackstageButton.vue';
import { useGrid } from '@/composables/grid';
import { useNotification } from '@/composables/useNotification';
import { ButtonVariant } from '@/models/button-variant.enum';
import { CacheAdminService } from '@/services/cache-admin.service';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import type { GridApi } from '@ag-grid-community/core';
import { AgGridVue } from '@ag-grid-community/vue3';
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  pattern?: string;
}

const props = withDefaults(defineProps<Props>(), {
  pattern: '*',
});

const emit = defineEmits<{
  'keys-deleted': [];
}>();

const { t } = useI18n();
const { defaultGridOptions } = useGrid();
const { notifySuccess, notifyError } = useNotification();
const cacheAdminService = new CacheAdminService();

const loading = ref(false);
const columnDefinitions = ref<any[]>([]);
const rowData = ref<any[]>([]);
const gridApi = ref<GridApi | undefined>(undefined);
const gridOptions = { ...defaultGridOptions(), rowSelection: 'multiple' };
const modules = [ClientSideRowModelModule];
const defaultColDef = {
  resizable: true,
  filter: true,
  sortable: true,
};
const pageSize = 20;
const selectedCount = ref(0);
const cursor = ref('0');
const hasMore = ref(false);

function setColumnDefinitions() {
  columnDefinitions.value = [
    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      width: 40,
      suppressHeaderMenuButton: true,
      sortable: false,
      filter: false,
    },
    {
      headerValueGetter: () => t('admin.cache.key_column'),
      field: 'key',
      flex: 1,
      cellStyle: { textAlign: 'left' },
    },
  ];
}

async function loadKeys(append = false) {
  loading.value = true;

  const result = await cacheAdminService.getKeys(props.pattern, append ? cursor.value : '0');

  rowData.value = append
    ? [...rowData.value, ...result.keys.map((key) => ({ key }))]
    : result.keys.map((key) => ({ key }));
  cursor.value = result.cursor;
  hasMore.value = result.hasMore;
  loading.value = false;

  setColumnDefinitions();
}

async function loadMore() {
  await loadKeys(true);
}

function onFirstDataRendered(params: any) {
  gridApi.value = params.api;
  params.api.sizeColumnsToFit();
}

function onSelectionChanged() {
  selectedCount.value = gridApi.value?.getSelectedRows().length ?? 0;
}

function handleResize() {
  if (typeof window !== 'undefined') {
    gridApi.value?.sizeColumnsToFit();
  }
}

async function deleteSelected() {
  const selectedRows = gridApi.value?.getSelectedRows() ?? [];
  const keys = selectedRows.map((row) => row.key);

  if (!keys.length) {
    return;
  }

  if (!window.confirm(t('admin.cache.delete_selected_confirm'))) {
    return;
  }

  const result = await cacheAdminService.deleteKeys(keys);

  if (result.success) {
    notifySuccess(t('admin.cache.keys_deleted'));
    await loadKeys();
    emit('keys-deleted');
  } else {
    notifyError(t('admin.cache.error_deleting_keys'));
  }
}

onMounted(async () => {
  await loadKeys();
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', handleResize);
  }
});

onBeforeUnmount(() => {
  gridApi.value = undefined;
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', handleResize);
  }
});

defineExpose({
  loading,
  rowData,
  gridApi,
  selectedCount,
  hasMore,
  loadKeys,
  loadMore,
  deleteSelected,
});
</script>
<style lang="scss" scoped>
.grid-cache-keys {
  margin-bottom: 40px;

  .buttons-cnt {
    padding: 10px 0 30px;
    text-align: right;
  }

  .load-more-cnt {
    padding: 20px 0 0;
    text-align: center;
  }

  .no-keys-found {
    padding: 40px 0;
    text-align: center;
    color: var(--secondary-text-color);
  }
}
</style>
